import { useInView } from '../../hooks/useInView'
import { GOALS, reachGoal } from '../../lib/metrika'
import registerImg from '../../assets/dashboard/register.png'
import billingImg from '../../assets/dashboard/billing.jpg'
import setupImg from '../../assets/dashboard/setup.png'
import launchImg from '../../assets/dashboard/launch.jpg'
import leadsImg from '../../assets/dashboard/leads.jpg'
import analyticsImg from '../../assets/dashboard/analytics.jpg'

const CARD_H = {
  background: 'linear-gradient(90deg, #e8f9fe 0%, #caf0f8 100%)',
  border: '1px solid #d3d5d8',
}

const CARD_V = {
  background: 'linear-gradient(180deg, #effbfe 0%, #caf0f8 100%)',
  border: '1px solid #d3d5d8',
}

function fadeUp(inView: boolean, delay: number) {
  return {
    opacity: inView ? 1 : 0,
    transform: inView ? 'translateY(0)' : 'translateY(28px)',
    transition: `opacity 0.65s cubic-bezier(0.45,0,0.15,1) ${delay}ms, transform 0.65s cubic-bezier(0.45,0,0.15,1) ${delay}ms`,
  }
}

function slideIn(inView: boolean, fromLeft: boolean, delay: number) {
  return {
    opacity: inView ? 1 : 0,
    transform: inView ? 'translateX(0)' : `translateX(${fromLeft ? '-60px' : '60px'})`,
    transition: `opacity 0.65s cubic-bezier(0.45,0,0.15,1) ${delay}ms, transform 0.65s cubic-bezier(0.45,0,0.15,1) ${delay}ms`,
  }
}

function CardTitle({ children }: { children: React.ReactNode }) {
  return (
    <p className="font-body mb-[11px] text-[22px] font-medium leading-[28px] tracking-[0.3px] text-[#041527] lg:text-[24px]">
      {children}
    </p>
  )
}

function CardDesc({ children }: { children: React.ReactNode }) {
  return (
    <p className="font-body text-[15px] leading-[22px] text-[#394959] sm:text-[16px] sm:leading-6">{children}</p>
  )
}

// Мобильные карточки по Figma (фреймы 2126:1243…1281, ширина 340).
// Геометрия задана в cqw (% от ширины карточки) → всё масштабируется пропорционально на любой ширине.
// У первых двух карточек `it` не задан: картинка прижата к низу вместо координаты из макета.
interface MobileCardData {
  title: string
  desc: React.ReactNode
  img: string
  alt: string
  ratio: string // aspect-ratio карточки (340 / высота фрейма)
  nowrap?: boolean
  ty: string // отступ текста сверху
  tx: string // отступ текста слева
  il: string // картинка: left
  it?: string // картинка: top; если не задан — картинка прижата к низу карточки
  iw: string // картинка: width
  ih: string // картинка: height
  ir: string // картинка: радиус углов
}

const MOBILE_CARDS: MobileCardData[] = [
  {
    title: 'Быстрая регистрация', desc: 'Займёт пару минут', img: registerImg, alt: 'Форма регистрации',
    ratio: '340 / 344', nowrap: true, tx: '8.82cqw', ty: '7.94cqw',
    il: '8.82cqw', iw: '81.76cqw', ih: '61.76cqw', ir: '2.35cqw',
  },
  {
    title: 'Пополнение баланса', desc: 'Оплата по счёту в вашем кабинете', img: billingImg, alt: 'Диалог пополнения баланса',
    ratio: '340 / 375', nowrap: true, tx: '8.82cqw', ty: '7.94cqw',
    il: '8.24cqw', iw: '83.24cqw', ih: '70.6cqw', ir: '3.24cqw',
  },
  {
    title: 'Настройка акции', desc: 'Картинка, условия и города', img: setupImg, alt: 'Форма настройки акции',
    ratio: '340 / 375', nowrap: true, tx: '8.82cqw', ty: '7.94cqw',
    il: '8.82cqw', it: '38.53cqw', iw: '110cqw', ih: '62.35cqw', ir: '2.35cqw',
  },
  {
    title: 'Запуск и остановка', desc: 'Вы управляете состоянием, главное, чтобы на балансе были деньги', img: launchImg, alt: 'Список рекламных кампаний',
    ratio: '340 / 375', tx: '8.82cqw', ty: '7.94cqw',
    il: '8.82cqw', it: '39.12cqw', iw: '121.18cqw', ih: '61.47cqw', ir: '2.35cqw',
  },
  {
    title: 'Моментальные заявки',
    desc: <>Клиент забрал подарок → его контакты сразу в&nbsp;кабинете.<br />Лиды без&nbsp;задержек.</>,
    img: leadsImg, alt: 'Таблица активаций',
    ratio: '340 / 406', nowrap: true, tx: '9.41cqw', ty: '11.32cqw',
    il: '9.12cqw', it: '48.53cqw', iw: '102.65cqw', ih: '61.76cqw', ir: '2.65cqw',
  },
  {
    title: 'Понятная эффективность', desc: 'Просмотры, клики и конверсии обновляются ежедневно.', img: analyticsImg, alt: 'Аналитический дашборд',
    ratio: '340 / 406', tx: '9.41cqw', ty: '11.47cqw',
    il: '8.53cqw', it: '48.24cqw', iw: '101.47cqw', ih: '62.35cqw', ir: '2.35cqw',
  },
]

function MobileCard({ c, style }: { c: MobileCardData; style?: React.CSSProperties }) {
  return (
    <div
      className="relative w-full overflow-hidden rounded-[20px] border border-[#d3d5d8]"
      style={{
        aspectRatio: c.ratio,
        containerType: 'inline-size',
        background: 'linear-gradient(180deg, #effbfe 0%, #caf0f8 100%)',
        ...style,
      }}
    >
      <div className="absolute" style={{ left: c.tx, top: c.ty, width: '85cqw' }}>
        <p
          className="font-body font-bold text-[#041527]"
          style={{ fontSize: '7.11cqw', lineHeight: '8.1cqw', letterSpacing: '0.09cqw', whiteSpace: c.nowrap ? 'nowrap' : 'normal' }}
        >
          {c.title}
        </p>
        <p className="font-body text-[#394959]" style={{ marginTop: '3.24cqw', fontSize: '4.71cqw', lineHeight: '7.06cqw' }}>
          {c.desc}
        </p>
      </div>
      <img
        src={c.img}
        alt={c.alt}
        className="absolute object-cover"
        style={{
          left: c.il,
          ...(c.it ? { top: c.it } : { bottom: 0 }),
          width: c.iw,
          height: c.ih,
          // прижатая к низу картинка не должна иметь скруглённые нижние углы вплотную к краю карточки
          borderRadius: c.it ? c.ir : `${c.ir} ${c.ir} 0 0`,
          objectPosition: 'left top',
        }}
      />
    </div>
  )
}

export function Dashboard() {
  const { ref, inView } = useInView(0.8, '0px 0px -80px 0px')
  return (
    <section
      id="dashboard"
      className="mx-auto flex max-w-[1920px] flex-col items-center gap-8 px-4 py-14 sm:gap-[42px] sm:px-6 sm:py-20 lg:px-[7%] lg:py-28"
    >
      {/* Header */}
      <div ref={ref} className="flex flex-col items-center gap-5 text-center" style={fadeUp(inView, 0)}>
        <span
          className="inline-block text-[12px] font-bold uppercase text-accent"
          style={{ letterSpacing: '2px', lineHeight: '24px', fontFamily: 'Helvetica, Arial, Inter, system-ui, sans-serif' }}
        >
          О&nbsp;личном кабинете и&nbsp;запуске
        </span>
        <h2 className="font-display text-[28px] max-[359px]:text-[24px] font-semibold leading-[1.14] text-[#041527] sm:text-[44px] lg:text-[56px] lg:leading-[64px]">
          Всё управление{' '}
          <br className="hidden sm:block" />
          в&nbsp;ваших руках
        </h2>
        <p className="font-body max-w-[748px] text-[15px] leading-[23px] text-[#676b94] sm:text-[16px] sm:leading-[26px] lg:text-[20px] lg:leading-[30px]">
          Никаких долгих брифов и&nbsp;звонков менеджерам. Зарегистрируйтесь, настройте акцию и&nbsp;запустите показы&nbsp;— полностью{' '}самостоятельно.
        </p>
      </div>

      {/* Cards — мобилка: вертикальный стек 6 карточек точь-в-точь по Figma */}
      <div className="flex w-full flex-col gap-4 lg:hidden">
        {MOBILE_CARDS.map((c, i) => (
          <MobileCard key={c.title} c={c} style={fadeUp(inView, 100 + i * 80)} />
        ))}
      </div>

      {/* Cards — десктоп: bento-сетка */}
      <div className="hidden w-full flex-col gap-4 lg:flex">
        {/* Top row */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">

          {/* Left column — slides from left */}
          <div className="flex flex-col gap-4 lg:h-full" style={slideIn(inView, true, 100)}>

            {/* Быстрая регистрация */}
            <div className="relative overflow-hidden rounded-[20px] lg:flex-1" style={CARD_H}>
              <div className="px-7 py-7 lg:hidden">
                <CardTitle>Быстрая регистрация</CardTitle>
                <CardDesc>Займёт пару минут</CardDesc>
                <div className="mt-4 h-[180px] overflow-hidden rounded-[8px]">
                  <img src={registerImg} alt="Форма регистрации" className="w-full" />
                </div>
              </div>
              <div className="absolute left-7 top-1/2 hidden -translate-y-1/2 max-w-[36%] lg:block">
                <CardTitle>Быстрая регистрация</CardTitle>
                <CardDesc>Займёт пару минут</CardDesc>
              </div>
              <img
                src={registerImg}
                alt="Форма регистрации"
                className="absolute right-6 bottom-0 hidden w-[253px] rounded-t-[8px] shadow-sm lg:block"
              />
            </div>

            {/* Пополнение баланса */}
            <div className="relative overflow-hidden rounded-[20px] lg:flex-1" style={CARD_H}>
              <div className="px-7 py-7 lg:hidden">
                <CardTitle>Пополнение баланса</CardTitle>
                <CardDesc>Оплата по&nbsp;счёту в&nbsp;вашем кабинете</CardDesc>
                <img src={billingImg} alt="Диалог пополнения баланса" className="mt-4 h-auto w-full rounded-[8px]" />
              </div>
              <div className="absolute left-7 top-1/2 hidden -translate-y-1/2 max-w-[36%] lg:block">
                <CardTitle>Пополнение баланса</CardTitle>
                <CardDesc>Оплата по&nbsp;счёту в&nbsp;вашем кабинете</CardDesc>
              </div>
              <img
                src={billingImg}
                alt="Диалог пополнения баланса"
                className="absolute right-7 top-4 hidden w-[249px] max-w-none rounded-[8px] shadow-sm lg:block"
              />
            </div>

          </div>

          {/* Right column — slides from right */}
          <div
            className="relative overflow-hidden rounded-[20px] lg:h-[460px]"
            style={{ ...CARD_V, ...slideIn(inView, false, 150) }}
          >
            <div className="px-7 py-7 lg:hidden">
              <CardTitle>Настройка акции</CardTitle>
              <CardDesc>Картинка, условия и&nbsp;города</CardDesc>
              <img src={setupImg} alt="Форма настройки акции" className="mt-4 h-auto w-full rounded-[8px]" />
            </div>
            <div className="absolute left-8 top-[36px] hidden lg:block">
              <CardTitle>Настройка акции</CardTitle>
              <CardDesc>Картинка, условия и&nbsp;города</CardDesc>
            </div>
            <img
              src={setupImg}
              alt="Форма настройки акции"
              className="absolute left-[32px] top-[108px] hidden w-[477px] rounded-[8px] lg:block"
            />
          </div>

        </div>

        {/* Bottom row — Figma: текст слева сверху, крупный скриншот прижат влево и обрезается справа */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {[
            {
              title: 'Запуск и остановка',
              desc: 'Вы управляете состоянием, главное, чтобы на балансе были деньги',
              img: launchImg,
              alt: 'Список рекламных кампаний',
            },
            {
              title: 'Моментальные заявки',
              desc: 'Клиент забрал подарок — его контакты сразу в кабинете. Лиды без задержек.',
              img: leadsImg,
              alt: 'Таблица активаций',
            },
            {
              title: 'Понятная эффективность',
              desc: 'Просмотры, клики и конверсии обновляются ежедневно.',
              img: analyticsImg,
              alt: 'Аналитический дашборд',
            },
          ].map((card, i) => (
            <div
              key={card.title}
              className="relative overflow-hidden rounded-[20px] lg:h-[450px] 2xl:h-[500px]"
              style={{ ...CARD_V, ...fadeUp(inView, 250 + i * 100) }}
            >
              {/* Mobile: текст + картинка целиком стопкой */}
              <div className="px-7 py-7 lg:hidden">
                <CardTitle>{card.title}</CardTitle>
                <CardDesc>{card.desc}</CardDesc>
                <img src={card.img} alt={card.alt} className="mt-4 h-auto w-full rounded-[8px]" />
              </div>
              {/* Desktop: текст слева сверху */}
              <div className="absolute left-8 right-6 top-[39px] hidden lg:block">
                <CardTitle>{card.title}</CardTitle>
                <CardDesc>{card.desc}</CardDesc>
              </div>
              {/* Desktop: крупный скриншот, прижат влево, обрезается справа через overflow-hidden */}
              <img
                src={card.img}
                alt={card.alt}
                className="absolute left-8 top-[180px] hidden h-[235px] w-auto max-w-none rounded-[8px] shadow-sm lg:block 2xl:h-[285px]"
              />
            </div>
          ))}
        </div>
      </div>

      {/* CTA */}
      <a
        href="https://lk.ris.promo/auth/register"
        onClick={() => reachGoal(GOALS.signupClick, { place: 'секция о кабинете' })}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center justify-center rounded-full px-5 py-2.5 text-[16px] font-medium text-[#fcfcfc] transition hover:scale-[1.05] hover:brightness-110 active:scale-[0.97]"
        style={{ background: 'linear-gradient(180deg, #0d6fe0 0%, #145aff 100%)', ...fadeUp(inView, 550) }}
      >
        Создать аккаунт
      </a>
    </section>
  )
}
